import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { Mic, X } from "lucide-react";
import { ModelInfo } from "@jammable/clientapi";
import { customModelUrl } from "@jammable/ui-core";
import { getSubscriptionStatus } from "@jammable/ui-payments-feature/src/utils";
import CoverUpsellSheet, {
  CoverUpsellOffer,
  useUpgradePreview,
  yearlyFigures,
} from "../../../../../../../apps/web/components/upsell/CoverUpsellSheet";
import { track } from "../../../../../../../apps/web/utils/analytics";
import { VoiceImage } from "../../utils";

// Upsells to Basic subscribers on their own cover: one "While you wait" card
// on the loader, and "Now hear it in your own voice" on the finished cover.
// Same ink card as CreateVideo so they read as part of the page, soft copy,
// always a "Not now". The button opens CoverUpsellSheet, which states the
// price before anything is charged.

export type UpsellCardId = "train" | "train-own" | "video" | "yearly";
export type UpsellPlacement = "loader" | "ready";

const OFFER: Record<UpsellCardId, CoverUpsellOffer> = {
  train: "creator",
  "train-own": "creator",
  video: "creator",
  yearly: "yearly",
};

const DISMISSED_KEY = "jammable:cover-upsell:dismissed";

const readDismissed = (): string[] => {
  try {
    return JSON.parse(sessionStorage.getItem(DISMISSED_KEY) || "[]");
  } catch {
    return [];
  }
};

// Stable per cover, so a refresh or the next status poll doesn't swap cards.
const hash = (s: string) =>
  [...s].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7);

/**
 * Which card to show, or null. Owners on Basic (and the secret early adopter
 * plans) only; the yearly card only when upgrade-preview prices it, which
 * means monthly Basic with an annual price in their currency.
 */
export function useCoverUpsell({
  placement,
  conversionId,
  subscriptions,
  enabled = true,
}: {
  placement: UpsellPlacement;
  conversionId?: string;
  subscriptions?: any;
  enabled?: boolean;
}) {
  const plan = subscriptions ? getSubscriptionStatus(subscriptions) : null;
  const creatorOk = enabled && (plan === "basic" || plan === "early adopter");
  const yearlyOk = enabled && plan === "basic" && placement === "loader";

  let pick: UpsellCardId = "train";
  if (placement === "loader") {
    const cards: UpsellCardId[] = ["train", "train-own", "video"];
    if (yearlyOk) cards.push("yearly");
    pick = cards[hash(conversionId || "") % cards.length];
  }

  const yearly = useUpgradePreview("yearly", yearlyOk && pick === "yearly");
  // Read after mount: sessionStorage isn't there on the server.
  const [dismissed, setDismissed] = useState<string[] | null>(null);
  useEffect(() => setDismissed(readDismissed()), []);

  if (pick === "yearly" && yearly.isError) pick = "train";
  const key = `${placement}:${pick}`;

  const card =
    !creatorOk ||
    dismissed === null ||
    dismissed.includes(key) ||
    (pick === "yearly" && !yearly.data)
      ? null
      : pick;

  const dismiss = () => {
    const next = [...readDismissed(), key];
    try {
      sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(next));
    } catch {
      // Private mode: it stays dismissed until the page reloads.
    }
    setDismissed(next);
  };

  return { card, dismiss, yearlyPreview: yearly.data };
}

const Tile = ({
  card,
  voice,
  yearly,
}: {
  card: UpsellCardId;
  voice?: Partial<ModelInfo>;
  yearly?: ReturnType<typeof yearlyFigures>;
}) => {
  const face = (
    <span className="tw-relative tw-h-12 tw-w-12 tw-shrink-0 tw-overflow-hidden tw-rounded-full tw-bg-jam-deep tw-ring-[3px] tw-ring-jam-lime">
      {voice && (
        <VoiceImage
          model={voice}
          fill
          sizes="48px"
          className="tw-object-cover"
        />
      )}
    </span>
  );
  const you = (big?: boolean) => (
    <span
      className={`tw-relative tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-[3px] tw-border-dashed tw-border-jam-lime tw-bg-jam-deep tw-text-jam-lime ${big ? "tw-h-16 tw-w-16" : "tw--mt-[18px] tw-h-12 tw-w-12"}`}
    >
      <Mic size={big ? 26 : 20} strokeWidth={2.6} />
    </span>
  );
  const chip = (text: string) => (
    <span className="tw-max-w-[76px] tw-truncate tw-whitespace-nowrap tw-rounded-full tw-bg-white tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-black tw-text-jam-ink md:tw-max-w-[88px]">
      {text}
    </span>
  );

  return (
    <div className="tw-relative tw-flex tw-h-[150px] tw-w-[84px] tw-shrink-0 tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-overflow-hidden tw-rounded-[18px] tw-bg-gradient-to-b tw-from-jam-deep tw-to-jam-purple md:tw-h-[170px] md:tw-w-[96px]">
      {card === "yearly" && yearly ? (
        <>
          {yearly.monthly && (
            <s className="tw-text-[13px] tw-font-extrabold tw-text-jam-soft">
              {yearly.monthly}
            </s>
          )}
          <b className="jam-display tw-text-center tw-text-[26px] tw-font-extrabold tw-leading-[0.9] tw-tracking-[-0.04em] tw-text-jam-lime md:tw-text-[30px]">
            {yearly.perMonth}
          </b>
          <span className="tw--mt-2 tw-text-[11px] tw-font-extrabold tw-text-[#E3D3F7]">
            a month
          </span>
        </>
      ) : card === "train" ? (
        <>
          <span className="tw-flex tw-flex-col tw-items-center">
            {face}
            {you()}
          </span>
          {chip(`you + ${voice?.artistname?.trim() || "them"}`)}
        </>
      ) : card === "train-own" ? (
        <>
          {you(true)}
          {chip("only yours")}
        </>
      ) : (
        <>
          {face}
          {chip("la la la")}
        </>
      )}
    </div>
  );
};

export function UpsellCard({
  card,
  placement,
  voice,
  yearlyPreview,
  onDismiss,
}: {
  card: UpsellCardId;
  placement: UpsellPlacement;
  voice?: Partial<ModelInfo>;
  yearlyPreview?: Parameters<typeof yearlyFigures>[0];
  onDismiss: () => void;
}) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const source = `cover-${placement}:${card}`;
  const name = voice?.artistname?.trim();
  const yearly = yearlyPreview ? yearlyFigures(yearlyPreview) : undefined;

  const shown = useRef(false);
  useEffect(() => {
    if (shown.current) return;
    shown.current = true;
    track("Cover Upsell Shown", { card, placement });
  }, [card, placement]);

  const copy = {
    train: {
      title:
        placement === "ready"
          ? "Now hear it in your own voice"
          : name
            ? `Next time, sing it with ${name}`
            : "Next time, sing it with them",
      sub: "Train a voice from a few minutes of your own singing or talking, then use it in any cover.",
      cta: "Make my voice",
      sheet: "Train your own voices",
    },
    "train-own": {
      title: "Make a voice nobody else has",
      sub: "A few minutes of you singing or talking is all it takes. Yours to use in every cover.",
      cta: "Make my voice",
      sheet: "Train your own voices",
    },
    video: {
      title: "Want it as a video?",
      sub: "Marble Run and lyric videos of your covers in 1080p, 50 a month on Pro.",
      cta: "See Pro",
      sheet: "Make videos of your covers",
    },
    yearly: {
      title: "Same Basic, way less",
      sub: yearly
        ? `Switch to yearly and pay ${yearly.perMonth} a month${yearly.monthly ? ` instead of ${yearly.monthly}` : ""}. This month's payment counts.`
        : "",
      cta: yearly ? `Switch to ${yearly.perMonth}/mo` : "Switch to yearly",
      sheet: undefined,
    },
  }[card];

  const dismiss = () => {
    track("Cover Upsell Dismissed", { card, placement });
    onDismiss();
  };

  return (
    <div className="tw-relative tw-flex tw-items-center tw-gap-4 tw-rounded-[26px] tw-bg-jam-ink tw-p-4 tw-text-white md:tw-rounded-[30px] md:tw-p-5">
      <button
        type="button"
        aria-label="Dismiss"
        onClick={dismiss}
        className="tw-absolute tw-right-3 tw-top-3 tw-flex tw-h-7 tw-w-7 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-p-0 tw-text-jam-soft tw-transition-colors hover:tw-text-white"
      >
        <X size={16} strokeWidth={2.6} />
      </button>
      <Tile card={card} voice={voice} yearly={yearly} />
      <div className="tw-flex tw-min-w-0 tw-flex-col tw-items-start tw-gap-2 tw-pr-5">
        <span className="tw-text-[10px] tw-font-extrabold tw-uppercase tw-tracking-[0.06em] tw-text-jam-lime">
          {placement === "ready" ? "Your cover's ready" : "While you wait"}
        </span>
        <h2 className="jam-display tw-m-0 tw-text-[22px] tw-font-extrabold tw-leading-[1.08] tw-text-inherit">
          {copy.title}
        </h2>
        <div className="tw-text-[13px] tw-font-semibold tw-leading-[1.45] tw-text-jam-soft">
          {copy.sub}
        </div>
        <div className="tw-mt-0.5 tw-flex tw-items-center tw-gap-3.5">
          <button
            type="button"
            onClick={() => {
              track("Cover Upsell Clicked", { card, placement });
              setOpen(true);
            }}
            className="tw-cursor-pointer tw-whitespace-nowrap tw-rounded-full tw-border-0 tw-bg-jam-lime tw-px-4 tw-py-2.5 tw-text-[13px] tw-font-extrabold tw-text-jam-ink tw-transition hover:tw-brightness-95"
          >
            {copy.cta}
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="tw-cursor-pointer tw-whitespace-nowrap tw-border-0 tw-bg-transparent tw-p-0 tw-text-xs tw-font-extrabold tw-text-jam-soft tw-opacity-80 hover:tw-opacity-100"
          >
            Not now
          </button>
        </div>
      </div>
      <CoverUpsellSheet
        open={open}
        offer={OFFER[card]}
        source={source}
        title={copy.sheet}
        coverMaking={placement === "loader"}
        onClose={() => setOpen(false)}
        onUpgraded={() => {
          setOpen(false);
          // Still Basic after the yearly switch; don't swap in another card.
          if (card === "yearly") onDismiss();
          // From the finished cover, straight on to training. On the loader
          // they stay with the cover they're waiting for.
          if (placement === "ready" && OFFER[card] === "creator")
            router.push(customModelUrl());
        }}
      />
    </div>
  );
}

/** The card for this spot, or nothing. */
export function CoverUpsell({
  placement,
  conversionId,
  subscriptions,
  voice,
}: {
  placement: UpsellPlacement;
  conversionId?: string;
  subscriptions?: any;
  voice?: Partial<ModelInfo>;
}) {
  const { card, dismiss, yearlyPreview } = useCoverUpsell({
    placement,
    conversionId,
    subscriptions,
  });
  if (!card) return null;
  return (
    <UpsellCard
      card={card}
      placement={placement}
      voice={voice}
      yearlyPreview={yearlyPreview}
      onDismiss={dismiss}
    />
  );
}
